import { supabase } from '@/integrations/supabase/client';

export type PushType =
  | 'review_like'
  | 'list_like'
  | 'comment_on_review'
  | 'comment_reply'
  | 'new_follower'
  | 'tour_announced'
  | 'mention';

export interface PushPayload {
  type: PushType;
  recipient_user_id?: string;
  recipientUserId?: string;
  actor_user_id?: string | null;
  actorUserId?: string | null;
  entityId?: string;
  entityType?: string;
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
  /** Optional dedupe key. If provided with cooldown_seconds, send-push will skip duplicates within the window. */
  dedupe_key?: string;
  /** Cooldown window in seconds (default applied server-side when dedupe_key is set). */
  cooldown_seconds?: number;
}

/**
 * Fire-and-forget push notification via the generic send-push edge function.
 * Skips self-notifications client-side and never throws.
 */
export function sendPush(payload: PushPayload): void {
  const recipientUserId = payload?.recipient_user_id ?? payload?.recipientUserId;
  const actorUserId = payload?.actor_user_id ?? payload?.actorUserId ?? null;
  const entityData = {
    ...(payload?.entityId ? { entity_id: payload.entityId } : {}),
    ...(payload?.entityType ? { entity_type: payload.entityType } : {}),
    ...(payload?.data || {}),
  };

  if (!payload?.type || !recipientUserId) {
    console.warn('[push] skipped — missing type or recipient_user_id', payload);
    return;
  }
  if (actorUserId && actorUserId === recipientUserId) {
    console.log('[push] skipped — self', { type: payload.type });
    return;
  }
  const finalPayload = {
    type: payload.type,
    recipient_user_id: recipientUserId,
    actor_user_id: actorUserId,
    title: payload.title,
    body: payload.body,
    data: entityData,
    dedupe_key: payload.dedupe_key,
    cooldown_seconds: payload.cooldown_seconds,
  };
  console.log('[push] invoke send-push', {
    type: payload.type,
    recipient_user_id: recipientUserId,
    actor_user_id: actorUserId,
    dedupe_key: payload.dedupe_key ?? null,
    cooldown_seconds: payload.cooldown_seconds ?? null,
  });
  supabase.functions
    .invoke('send-push', { body: finalPayload })
    .then(({ data, error }) => {
      if (error) {
        console.warn('[push] invoke error', { type: payload.type, error });
      } else {
        console.log('[push] invoke result', { type: payload.type, data });
      }
    })
    .catch((err) => console.warn('[push] invoke threw', { type: payload.type, err }));
}
